import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Lock, Check, Clock, Star, Heart, Diamond, Info } from 'lucide-react';
import { AngelSkin } from './AngelSkinsShop';
interface SkinCarouselProps {
  skins: AngelSkin[];
  onSkinClick: (skin: AngelSkin) => void;
  onUnlock: (skin: AngelSkin) => void;
}
export const SkinCarousel: React.FC<SkinCarouselProps> = ({
  skins,
  onSkinClick,
  onUnlock
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const currentSkin = skins[currentIndex];
  // Handle next/previous slide
  const goToNext = () => {
    setCurrentIndex(prevIndex => (prevIndex + 1) % skins.length);
  };
  const goToPrev = () => {
    setCurrentIndex(prevIndex => (prevIndex - 1 + skins.length) % skins.length);
  };
  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
  };
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  // Helper function to render the rarity badge
  const renderRarityBadge = (rarity: string) => {
    const colors = {
      Common: 'bg-gray-500',
      Rare: 'bg-blue-500',
      Epic: 'bg-purple-500',
      Legendary: 'bg-gradient-to-r from-yellow-500 to-amber-600'
    };
    const colorClass = colors[rarity as keyof typeof colors] || colors.Common;
    return <div className={`${colorClass} text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center`} data-id="element-19538">
        <Star size={12} className="mr-1" fill="currentColor" data-id="element-19539" />
        {rarity}
      </div>;
  };
  // Helper function to render currency icon
  const renderCurrencyIcon = (type: string) => {
    return type === 'Love Tokens' ? <Heart size={16} className="text-pink-500" fill="currentColor" data-id="element-19540" /> : <Diamond size={16} className="text-blue-400" data-id="element-19541" />;
  };
  if (!currentSkin) return null;
  return <div ref={carouselRef} className="relative w-full" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-19542">
      {/* Navigation arrows */}
      <button className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors" onClick={goToPrev} data-id="element-19543">
        <ChevronLeft size={24} data-id="element-19544" />
      </button>
      <button className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors" onClick={goToNext} data-id="element-19545">
        <ChevronRight size={24} data-id="element-19546" />
      </button>
      {/* Carousel content */}
      <div className="flex flex-col md:flex-row md:items-stretch bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800" data-id="element-19547">
        {/* Image section */}
        <div className="relative w-full md:w-2/3 aspect-[4/3] md:aspect-auto" data-id="element-19548">
          <img src={currentSkin.preview_image_url} alt={currentSkin.skin_name} className={`w-full h-full object-cover ${currentSkin.status === 'Locked' ? 'grayscale opacity-70' : ''}`} onClick={() => onSkinClick(currentSkin)} data-id="element-19549" />
          {/* Locked overlay */}
          {currentSkin.status === 'Locked' && <div className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer" onClick={() => onSkinClick(currentSkin)} data-id="element-19550">
              <Lock size={48} className="text-white opacity-80" data-id="element-19551" />
            </div>}
          {/* FOMO label */}
          {currentSkin.fomo_label && <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-sm font-bold px-3 py-1 rounded-full z-10" data-id="element-19552">
              {currentSkin.fomo_label}
            </div>}
          {/* Countdown timer */}
          {currentSkin.time_limited && currentSkin.countdown && <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg flex items-center" data-id="element-19553">
              <Clock size={16} className="mr-2" data-id="element-19554" />
              {currentSkin.countdown}
            </div>}
          {/* Angel indicator */}
          <div className="absolute top-4 left-4 bg-gray-900/80 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full z-10 flex items-center" data-id="element-19555">
            <span className="mr-1" data-id="element-19556">👼</span>
            {currentSkin.angel_name}
          </div>
          {/* Active indicator */}
          {currentSkin.status === 'Active' && <div className="absolute top-4 right-4 bg-green-500 text-white text-sm px-3 py-1 rounded-full z-10 flex items-center" data-id="element-19557">
              <Star size={14} className="mr-1" fill="currentColor" data-id="element-19558" />
              Active
            </div>}
        </div>
        {/* Info section */}
        <div className="w-full md:w-1/3 p-6 flex flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-white" data-id="element-19559">
          <div className="flex items-center justify-between mb-2" data-id="element-19560">
            <h2 className="text-2xl font-bold" data-id="element-19561">{currentSkin.skin_name}</h2>
            {renderRarityBadge(currentSkin.rarity)}
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4" data-id="element-19562">
            {currentSkin.short_description}
          </p>
          <div className="space-y-4 mb-6" data-id="element-19563">
            <div data-id="element-19564">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1" data-id="element-19565">
                VISUAL THEME
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-19566">
                {currentSkin.visual_theme}
              </p>
            </div>
            <div data-id="element-19567">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1" data-id="element-19568">
                PERSONALITY
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-19569">
                {currentSkin.personality_tone}
              </p>
            </div>
            <div data-id="element-19570">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1" data-id="element-19571">
                UNLOCK METHOD
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center" data-id="element-19572">
                <Info size={14} className="mr-1 text-gray-500 dark:text-gray-400" data-id="element-19573" />
                {currentSkin.unlock_method}
              </p>
            </div>
          </div>
          <div className="mt-auto" data-id="element-19574">
            <div className="flex items-center justify-between mb-4" data-id="element-19575">
              <div className="flex items-center space-x-2" data-id="element-19576">
                {renderCurrencyIcon(currentSkin.currency_type)}
                <span className="font-bold text-xl" data-id="element-19577">{currentSkin.price}</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm" data-id="element-19578">
                  {currentSkin.currency_type}
                </span>
              </div>
              <div className="text-sm px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-19579">
                {currentSkin.status}
              </div>
            </div>
            {currentSkin.status === 'Locked' ? <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all" onClick={() => onUnlock(currentSkin)} data-id="element-19580">
                Unlock Now
              </button> : currentSkin.status === 'Unlocked' ? <button className="w-full py-3 bg-gradient-to-r from-green-600 to-teal-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-green-500/20 transition-all" onClick={() => onUnlock(currentSkin)} data-id="element-19581">
                Activate Skin
              </button> : <button className="w-full py-3 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 font-medium cursor-not-allowed" disabled data-id="element-19582">
                Currently Active
              </button>}
          </div>
        </div>
      </div>
      {/* Pagination dots */}
      <div className="flex justify-center mt-4 space-x-2" data-id="element-19583">
        {skins.map((_, index) => <button key={index} className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentIndex ? 'bg-pink-500' : 'bg-gray-300 dark:bg-gray-700'}`} onClick={() => setCurrentIndex(index)} data-id="element-19584" />)}
      </div>
      {/* Thumbnails */}
      <div className="mt-6 flex overflow-x-auto space-x-2 pb-2 scrollbar-hide" data-id="element-19585">
        {skins.map((skin, index) => <div key={index} className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden cursor-pointer ${index === currentIndex ? 'ring-2 ring-pink-500' : 'opacity-70'}`} onClick={() => setCurrentIndex(index)} data-id="element-19586">
            <img src={skin.preview_image_url} alt={skin.skin_name} className="w-full h-full object-cover" data-id="element-19587" />
            {skin.status === 'Locked' && <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-id="element-19588">
                <Lock size={12} className="text-white" data-id="element-19589" />
              </div>}
            {skin.status === 'Active' && <div className="absolute bottom-0 inset-x-0 bg-green-500/80 text-white text-[10px] text-center py-0.5" data-id="element-19590">
                Active
              </div>}
          </div>)}
      </div>
    </div>;
};